import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FileRejection } from 'react-dropzone';
import { mergeClasses } from 'minimal-shared/utils';

import { alpha, styled } from '@mui/material/styles';

import { fileData } from './utils';
import { uploadClasses } from './classes';
import { fData } from '../../../utils/file/format-number';

// ----------------------------------------------------------------------

type RejectionFilesProps = React.ComponentProps<typeof ListRoot> & {
  files?: readonly FileRejection[];
};

export function RejectionFiles({ files, sx, className, ...other }: RejectionFilesProps) {
  const { t } = useTranslation();

  const getErrorMessage = (errorCode: string, errorMessage: string): string => {
    const maxSizeMatch = errorMessage.match(/File is larger than (\d+)/);

    switch (errorCode) {
      case 'file-too-large':
        if (maxSizeMatch) {
          const bytes = parseInt(maxSizeMatch[1], 10);
          const maxSizeMB = Math.round(bytes / (1024 * 1024));
          return t('documents.upload.errors.fileTooLarge', { size: `${maxSizeMB}MB` });
        }
        return t('documents.upload.errors.fileTooLargeGeneral');
      case 'file-invalid-type':
        return t('documents.upload.errors.invalidFileType');
      case 'too-many-files':
        return t('documents.upload.errors.tooManyFiles');
      default:
        return errorMessage;
    }
  };

  return (
    <ListRoot
      className={mergeClasses([uploadClasses.uploadRejectionFiles, className])}
      sx={sx}
      {...other}
    >
      {files?.map(({ file, errors }) => {
        const { path, size } = fileData(file);

        return (
          <ListItem key={path}>
            <ItemTitle>
              {path} - {size ? fData(size) : ''}
            </ItemTitle>

            {errors.map((error) => (
              <ItemCaption key={error.code}>
                - {getErrorMessage(error.code, error.message)}
              </ItemCaption>
            ))}
          </ListItem>
        );
      })}
    </ListRoot>
  );
}

// ----------------------------------------------------------------------

const ListRoot = styled('ul')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: 'column',
  padding: theme.spacing(2),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  border: `dashed 1px ${theme.palette.error.main}`,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
}));

const ListItem = styled('li')(() => ({ display: 'flex', flexDirection: 'column' }));

const ItemTitle = styled('span')(({ theme }) => ({ ...theme.typography.subtitle2 }));

const ItemCaption = styled('span')(({ theme }) => ({ ...theme.typography.caption }));
